import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  Gift,
  Infinity as InfinityIcon,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  QrCode,
} from 'lucide-react';
import type { POSCustomer } from '@/types';
import { formatMoney } from '@/utils/currency';

type LoyaltyInfo = {
  balance?: number;
  value?: number;
  enrolled?: boolean;
  program_active?: boolean;
  tier?: { tier_name?: string; badge_color?: string } | null;
  lifetime_points_earned?: number;
};

type StoreLogin = {
  linked?: boolean;
  email?: string;
  approval_status?: string;
  has_password?: boolean;
  is_active?: boolean;
} | null;

interface CustomerMidCardsProps {
  customer: POSCustomer;
  shopIsUk: boolean;
  loyaltyInfo?: LoyaltyInfo | null;
  loyaltyQrDataUrl?: string | null;
  enrolMarketing: boolean;
  onEnrolMarketingChange: (v: boolean) => void;
  enrolBusy: boolean;
  onEnrol: () => void;
  onShowQr: () => void;
  /** Admin point adjust */
  adjustPts?: string;
  adjustReason?: string;
  onAdjustPtsChange?: (v: string) => void;
  onAdjustReasonChange?: (v: string) => void;
  adjustBusy?: boolean;
  onAdjust?: () => void;
  standing?: {
    credit_limit?: number;
    credit_available?: number | null;
    outstanding?: number;
  } | null;
  isAdmin: boolean;
  storeLogin?: StoreLogin;
  storeLoginLoading?: boolean;
  storeLoginOpen: boolean;
  onToggleStoreLogin: () => void;
  storeLoginEmail: string;
  storeLoginPassword: string;
  onStoreLoginEmail: (v: string) => void;
  onStoreLoginPassword: (v: string) => void;
  storeLoginBusy: boolean;
  onSaveStoreLogin: () => void;
  onDisableStoreLogin: () => void;
}

function AccordionRow({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden lg:hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </button>
      {open && <div className="px-4 pb-4 border-t border-slate-100 pt-3">{children}</div>}
    </div>
  );
}

function ContactBody({
  customer,
  shopIsUk,
}: {
  customer: POSCustomer;
  shopIsUk: boolean;
}) {
  const { t } = useTranslation();
  const wa = (customer.mobile || '').replace(/[^\d+]/g, '');
  return (
    <div className="space-y-3 text-sm">
      <div className="flex items-center gap-2 min-w-0">
        <Phone className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="truncate text-slate-800">{customer.mobile || '—'}</span>
        {wa && (
          <a
            href={`https://wa.me/${wa.replace(/^\+/, '')}`}
            target="_blank"
            rel="noreferrer"
            className="ml-auto p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"
            title="WhatsApp"
          >
            <MessageCircle className="w-4 h-4" />
          </a>
        )}
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <Mail className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="truncate text-slate-800">{customer.email || '—'}</span>
      </div>
      <div className="flex items-start gap-2">
        <MapPin className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
        <div className="space-y-1 min-w-0 text-slate-700">
          <p className="leading-snug">{customer.address || '—'}</p>
          <p className="text-xs text-slate-500">
            {[customer.city, shopIsUk ? customer.province : null, customer.postal_code]
              .filter(Boolean)
              .join(' · ') || t('customers.dashboard.noLocation', 'No city / postcode')}
          </p>
        </div>
      </div>
    </div>
  );
}

function AccountBody(props: {
  customer: POSCustomer;
  standing?: CustomerMidCardsProps['standing'];
  isAdmin: boolean;
  storeLogin?: StoreLogin;
  storeLoginLoading?: boolean;
  storeLoginOpen: boolean;
  onToggleStoreLogin: () => void;
  storeLoginEmail: string;
  storeLoginPassword: string;
  onStoreLoginEmail: (v: string) => void;
  onStoreLoginPassword: (v: string) => void;
  storeLoginBusy: boolean;
  onSaveStoreLogin: () => void;
  onDisableStoreLogin: () => void;
}) {
  const { t } = useTranslation();
  const limit = Number(props.standing?.credit_limit || props.customer.credit_limit || 0);
  const outstanding = Number(props.standing?.outstanding || 0);
  const available =
    props.standing?.credit_available != null
      ? Number(props.standing.credit_available)
      : limit > 0
        ? Math.max(0, limit - outstanding)
        : null;

  return (
    <div className="space-y-3">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1">
          {t('customers.accountLimit', 'Account Limit')}
        </p>
        {!limit ? (
          <div className="flex items-center gap-1.5 text-slate-800">
            <InfinityIcon className="w-4 h-4 text-sky-500" />
            <span className="font-semibold">
              {t('customers.accountUnlimited', 'Unlimited')}
            </span>
          </div>
        ) : (
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Limit</span>
              <span className="font-semibold tabular-nums">{formatMoney(limit)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">{t('customers.outstanding')}</span>
              <span className={`font-medium tabular-nums ${outstanding > 0 ? 'text-rose-600' : ''}`}>
                {formatMoney(outstanding)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">{t('customers.creditAvailable', 'Available')}</span>
              <span
                className={`font-semibold tabular-nums ${
                  outstanding > limit ? 'text-rose-600' : 'text-emerald-700'
                }`}
              >
                {formatMoney(available || 0)}
              </span>
            </div>
          </div>
        )}
      </div>

      {props.isAdmin && !props.customer.is_walk_in && (
        <div className="pt-2 border-t border-slate-100">
          <button
            type="button"
            className="w-full flex items-center justify-between text-left"
            onClick={props.onToggleStoreLogin}
          >
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Store login
              </p>
              <p className="text-xs text-slate-600 truncate">
                {props.storeLoginLoading
                  ? 'Loading…'
                  : props.storeLogin?.linked
                    ? `${props.storeLogin.email} · ${props.storeLogin.approval_status || 'Approved'}`
                    : 'Not enabled'}
              </p>
            </div>
            <span className="text-xs text-primary-700 font-medium">
              {props.storeLoginOpen ? 'Hide' : 'Manage'}
            </span>
          </button>
          {props.storeLoginOpen && (
            <div className="mt-2 space-y-2">
              <input
                className="input-field text-xs py-1.5"
                type="email"
                value={props.storeLoginEmail}
                onChange={(e) => props.onStoreLoginEmail(e.target.value)}
                placeholder="Store email"
              />
              <input
                className="input-field text-xs py-1.5"
                type="password"
                value={props.storeLoginPassword}
                onChange={(e) => props.onStoreLoginPassword(e.target.value)}
                placeholder={
                  props.storeLogin?.has_password
                    ? 'New password (optional)'
                    : 'Password (letter + number)'
                }
                autoComplete="new-password"
              />
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  className="btn-primary text-[11px] px-2 py-1"
                  disabled={props.storeLoginBusy}
                  onClick={props.onSaveStoreLogin}
                >
                  {props.storeLogin?.linked ? 'Update' : 'Enable'}
                </button>
                {props.storeLogin?.linked && props.storeLogin?.is_active ? (
                  <button
                    type="button"
                    className="btn-secondary text-[11px] px-2 py-1"
                    disabled={props.storeLoginBusy}
                    onClick={props.onDisableStoreLogin}
                  >
                    Disable
                  </button>
                ) : null}
                <Link
                  to="/public-store?tab=customers"
                  className="btn-secondary text-[11px] px-2 py-1 inline-flex items-center"
                >
                  Signups
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CustomerMidCards(props: CustomerMidCardsProps) {
  const { t } = useTranslation();
  const {
    customer,
    shopIsUk,
    loyaltyInfo,
    loyaltyQrDataUrl,
    enrolMarketing,
    onEnrolMarketingChange,
    enrolBusy,
    onEnrol,
    onShowQr,
    adjustPts = '',
    adjustReason = '',
    onAdjustPtsChange,
    onAdjustReasonChange,
    adjustBusy = false,
    onAdjust,
    isAdmin,
  } = props;

  const pts = Math.floor(Number(loyaltyInfo?.balance) || 0);
  const enrolled = Boolean(loyaltyInfo?.enrolled);
  const programOn = loyaltyInfo?.program_active !== false;

  const loyaltyCard = (
    <div className="rounded-2xl border border-sky-200/60 bg-gradient-to-br from-sky-600 via-sky-500 to-indigo-600 text-white p-4 shadow-md relative overflow-hidden">
      <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/10" />
      <div className="relative flex gap-3 items-start">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2">
            <Gift className="w-4 h-4 text-sky-100" />
            <span className="text-[11px] font-semibold uppercase tracking-wide text-sky-100">
              Loyalty member
            </span>
            {enrolled && loyaltyInfo?.tier?.tier_name && (
              <span
                className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white/95 border border-white/30"
                style={{ backgroundColor: loyaltyInfo.tier.badge_color || 'rgba(0,0,0,0.25)' }}
              >
                {loyaltyInfo.tier.tier_name}
              </span>
            )}
          </div>
          <p className="text-3xl font-bold tabular-nums leading-none">
            {pts}
            <span className="text-sm font-medium text-sky-100 ml-1">pts</span>
          </p>
          <p className="text-sm text-sky-50/90">
            {loyaltyInfo?.value != null ? formatMoney(Number(loyaltyInfo.value) || 0) : '—'}
            {' · '}
            {enrolled ? 'Enrolled' : 'Not enrolled'}
            {!programOn ? ' · off' : ''}
          </p>
          {!customer.is_walk_in && !enrolled && programOn && (
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <label className="inline-flex items-center gap-1 text-[11px] text-sky-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enrolMarketing}
                  onChange={(e) => onEnrolMarketingChange(e.target.checked)}
                  className="rounded border-sky-200 h-3 w-3"
                />
                Marketing
              </label>
              <button
                type="button"
                disabled={enrolBusy}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-white text-sky-700 hover:bg-sky-50"
                onClick={onEnrol}
              >
                {enrolBusy ? '…' : 'Enrol'}
              </button>
            </div>
          )}
          {enrolled && (
            <div className="flex gap-2 pt-1 flex-wrap">
              <button
                type="button"
                onClick={onShowQr}
                className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/20 inline-flex items-center gap-1"
              >
                <QrCode className="w-3.5 h-3.5" /> View Card
              </button>
            </div>
          )}
          {isAdmin && enrolled && onAdjust && (
            <div className="flex gap-1.5 pt-2 flex-wrap">
              <input
                type="number"
                className="rounded-lg bg-white/15 border border-white/25 text-white placeholder:text-sky-100/70 text-[11px] px-2 py-1 w-20"
                placeholder="± pts"
                value={adjustPts}
                onChange={(e) => onAdjustPtsChange?.(e.target.value)}
              />
              <input
                className="rounded-lg bg-white/15 border border-white/25 text-white placeholder:text-sky-100/70 text-[11px] px-2 py-1 flex-1 min-w-[100px]"
                placeholder="Reason"
                value={adjustReason}
                onChange={(e) => onAdjustReasonChange?.(e.target.value)}
              />
              <button
                type="button"
                disabled={adjustBusy}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-white text-sky-700"
                onClick={onAdjust}
              >
                {adjustBusy ? '…' : 'Adjust'}
              </button>
            </div>
          )}
        </div>
        {enrolled && loyaltyQrDataUrl && (
          <button type="button" onClick={onShowQr} className="shrink-0">
            <img
              src={loyaltyQrDataUrl}
              alt="Loyalty QR"
              className="w-20 h-20 rounded-xl border-2 border-white/40 bg-white"
            />
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Desktop: 3-column mid row */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">
            {t('customers.sectionContact', 'Contact & Address')}
          </h4>
          <ContactBody customer={customer} shopIsUk={shopIsUk} />
        </div>
        {loyaltyCard}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-3">
            Account
          </h4>
          <AccountBody {...props} />
        </div>
      </div>

      {/* Tablet / mobile: loyalty full width, contact & account as accordions */}
      <div className="lg:hidden space-y-3">
        {loyaltyCard}
        <AccordionRow title={t('customers.sectionContact', 'Contact & Address')} defaultOpen>
          <ContactBody customer={customer} shopIsUk={shopIsUk} />
        </AccordionRow>
        <AccordionRow title="Account">
          <AccountBody {...props} />
        </AccordionRow>
      </div>
    </div>
  );
}
